import { useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useStateQuery } from "common/queries/hooks";
import { FetchError } from "common/fetch/errors";
import { PaymentErrorData } from "common/types/payments";
import { ClientCartDetail } from "common/types/carts";

import { ClientCheckout } from "client/carts/types";
import { sendPurchase } from "client/analytics/utils";
import { ClientCheckoutOrder } from "client/orders/types";
import { useInvalidateAllCartQueries } from "client/carts/hooks/invalidate";
import { useInvalidateOrders } from "client/orders/hooks/invalidate";

import { keys, urls } from "./constants";
import { useInvalidateClient } from "../../accounts/hooks/invalidate";

const noop = () => undefined;

export const useLastCheckoutQuery = () =>
  useStateQuery<ClientCheckoutOrder | null>({ queryKey: keys.lastCheckoutOrder(), default: null });

export const useCheckoutMutation = (
  cart: ClientCartDetail,
  {
    onSuccess = noop,
    onError = noop,
  }: { onSuccess?: () => void; onError?: (error: FetchError<void, PaymentErrorData>) => void } = {},
) => {
  const queryClient = useQueryClient();
  const invalidateCarts = useInvalidateAllCartQueries();
  const invalidateOrders = useInvalidateOrders();
  const invalidateClient = useInvalidateClient();
  return useFetchMutation<
    ClientCheckout,
    ClientCheckoutOrder,
    void,
    ClientCheckout,
    FetchError<void, PaymentErrorData>
  >({
    fetch: (body) => ({ method: "POST", url: urls.checkout(cart.id), body }),
    config: {
      onSuccess: (order) => {
        // Store new orderID to be displayed to user on order confirm page
        queryClient.setQueryData(keys.lastCheckoutOrder(), order);
        invalidateCarts();
        invalidateOrders();
        // if checkout created a new subscription, we wanna reset the client detail view since
        // each address there references the related subscriptions
        invalidateClient();
        onSuccess();
        sendPurchase(order, cart);
      },
      onError: (error) => {
        invalidateCarts();
        onError(error);
      },
    },
  });
};
